/**
 *
 * 将所有的异步处理放在这里
 * 场景包含：
 * 1. 通用版执行异步任务的tool
 * 2. 定制版异步任务：打标异步任务（后端合到通用版比较复杂，暂时先未合到通用版，所以接口都是两套）
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { AgentRun } from '@/lib/api';
import { AgentStatus } from '@/hooks/use-thread-agent';
import {
  NEED_CONTINUE_TAG_TASK_STATUS,
  TAG_TASK_TOOL_CALL_NAME_LIST,
} from '@/constants/agent';
import { useTagging } from '@/contexts/agent-tag-tool';
import { useAsyncTaskTool } from '@/contexts/async-task-tool-context';
import useModalMethod from '../use-modal-method';
import { useStores } from '@/store/hooks/useStore';
import { safeJsonParse } from '@/components/thread/utils';
import { NEED_CONTINUE_TASK_STATUS } from '@/constants/async-task-tool';
import { useI18n } from '@/hooks/use-i18n';

interface useAsyncTaskParams {
  checkAgentRuns: () => Promise<AgentRun[]>;
  setAgentRunId: (id: string | null) => void;
  setAgentStatus: (status: AgentStatus) => void;
}

export const useAsyncTask = ({
  checkAgentRuns,
  setAgentRunId,
  setAgentStatus,
}: useAsyncTaskParams) => {
  const { t, i18n } = useI18n(['agent', 'common']);
  const modal = useModalMethod();
  // 使用 useParams 获取 threadId
  const params = useParams();
  const threadId = params.threadId as string; // 从 params 对象中获取

  const stores = useStores();
  const { currentToolCall } = stores.threadToolStore;

  const { activeTaggingTask, stopTagging, updateTaggingInitInfo, getTagInfo } =
    useTagging();
  const {
    activeAsyncTask: _activeAsyncTask,
    lastAsyncTask: _lastAsyncTask,
    stop: _stopAsyncTask,
    getTaskInfo,
    updateTaskInitInfo,
    markTaskAsCompleted,
    checkTaskInteractive,
  } = useAsyncTaskTool();

  // 检查最后一个打标任务是否还在运行，需排除，最后一个消息为打标完成状态且需要展示继续执行按钮
  const checkLastTaskIsRunning = async (lastRun: AgentRun) => {
    let tagTaskIsRunning = false;
    // 打标任务
    if (lastRun?.status === 'async_tag_task') {
      const tagInfo = await getTagInfo(
        threadId,
        lastRun.metadata?.task_id || '',
      );
      if (tagInfo?.length) {
        tagTaskIsRunning = !NEED_CONTINUE_TAG_TASK_STATUS.includes(
          tagInfo[0]?.state,
        );
      }
    }
    // 通用版异步任务
    if (lastRun?.status === 'async_task') {
      const taskInfo = await getTaskInfo(
        threadId,
        lastRun.metadata?.task_id || '',
      );
      if (taskInfo?.length) {
        tagTaskIsRunning = !NEED_CONTINUE_TASK_STATUS.includes(
          taskInfo[0]?.state,
        );
      }
    }
    return tagTaskIsRunning;
  };

  const threadRunsHandler = useCallback(async () => {
    const agentRuns = await checkAgentRuns();

    const activeRun = agentRuns.find((run) => run.status === 'running');
    if (activeRun) {
      console.log('[PAGE] Found active run on load:', activeRun.id);
      setAgentRunId(activeRun.id);
    } else {
      console.log('[PAGE] No active agent runs found');

      setAgentStatus('idle');
    }
  }, [checkAgentRuns, checkLastTaskIsRunning, setAgentRunId, setAgentStatus]);

  // 监听，需要重新拉起会话的event (例如异步任务的各个环节需要后端通知我们重新拉起会话)
  useEffect(() => {
    const handleApiNotify = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { thread_id, event: _event, metadata } = customEvent?.detail;
      if (_event === 'thread_runs' && thread_id === threadId) {
        console.log('接收到thread_runs更新的event消息');
        // 调用agent_runs,重新check当前的状态
        threadRunsHandler();
      }
    };

    // Add event listener
    window.addEventListener('api-notify', handleApiNotify as EventListener);
    // Cleanup
    return () => {
      window.removeEventListener(
        'api-notify',
        handleApiNotify as EventListener,
      );
    };
  }, [threadId, threadRunsHandler]);

  //检查是否有异步任务在执行
  const checkAsyncTaskBeforeSend = useCallback(() => {
    if (!activeTaggingTask && !_activeAsyncTask) return Promise.resolve(true);
    let activeTaskId = activeTaggingTask?.task_id || _activeAsyncTask?.task_id;
    return new Promise<boolean>((resolve) => {
      modal.confirmTip({
        key: 'async-task-interrupt-warning',
        title: t('common:confirmInterrupt'),
        content: t('agent:page.taggingInterruptWarning'),
        okText: t('common:interrupt'),
        cancelText: t('common:cancel'),
        onCancel: () => {
          resolve(false);
        },
        onOk: async () => {
          if (activeTaggingTask) {
            stopTagging(activeTaskId);
          }
          if (_activeAsyncTask) {
            if (
              _activeAsyncTask?.features?.includes('edit_result') &&
              checkTaskInteractive(_activeAsyncTask?.task_id)
            ) {
              resolve(true);
            } else {
              _stopAsyncTask(activeTaskId);
            }
            // 标记异步任务为已完成
            await markTaskAsCompleted(activeTaskId);
          }
          resolve(true);
        },
      });
    });
  }, [
    activeTaggingTask,
    _activeAsyncTask,
    stopTagging,
    _stopAsyncTask,
    markTaskAsCompleted,
    checkTaskInteractive,
    t,
  ]);

  // 消息变化时，初始化去请求任务的基本信息用于展示信息,
  // 主要场景：流式返回过程中监测到有打标任务时，拿taskId去获取打标的基本信息用于页面展示
  const getToolCallsCb = (toolCalls: TOOL_CALL.RenderData[]) => {
    const _last =
      toolCalls?.length > 0 ? toolCalls[toolCalls.length - 1] : currentToolCall;
    const isTagTask = TAG_TASK_TOOL_CALL_NAME_LIST.includes(_last?.name);
    const isAsyncTask = _last?.metadata?.async_task;
    if (isTagTask || isAsyncTask) {
      const taskData = _last?.artifact?.attachments?.find(
        (i) => i.type === 'text',
      )?.data;

      const task = safeJsonParse(taskData, { task_id: '' });
      if (!task?.task_id) {
        return;
      }
      // 打标处理
      if (isTagTask) {
        updateTaggingInitInfo?.(task?.task_id);
      }
      // 异步任务处理
      if (isAsyncTask && !isTagTask) {
        updateTaskInitInfo?.(task?.task_id);
      }
    }
  };

  // 当前正在执行的异步任务
  const activeAsyncTask = activeTaggingTask || _activeAsyncTask;

  return {
    // 数据
    activeAsyncTask, // 当前正在执行的异步任务
    // 处理方法
    checkLastTaskIsRunning, // 检查最后一个异步任务是否还在运行
    checkAsyncTaskBeforeSend, // 检查是否有异步任务在执行
    getToolCallsCb, // 消息变化时，初始化去请求tagging任务的基本信息用于展示信息,
  };
};
